<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';

// Controle segmentado do `reasoning_effort`, do mais rápido (desligado) ao que mais pensa (alto).
// '' é o padrão do modelo: não é um nível, é não escolher. Por isso ele é o primeiro segmento,
// separado dos níveis por uma divisória, e não um chip que só aparece quando há nível escolhido
// (era um segundo jeito de escolher, com outra gramática).
const props = defineProps({
  modelValue: { type: String, default: '' },
  // Nome do modelo do par que não deixa desligar o raciocínio (o OpenRouter recusa `none`
  // nele). Com ele, o "Desligado" fica trancado.
  bloqueadoPor: { type: String, default: null },
  // O seletor acabou de voltar ao padrão porque o par mudou para um que exige raciocínio.
  voltouAoPadrao: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const niveis = computed(() => [
  {
    valor: '',
    rotulo: t('POWER_AGENTS.REASONING.DEFAULT_SHORT'),
    dica: t('POWER_AGENTS.REASONING.DEFAULT_HINT'),
  },
  {
    valor: 'none',
    rotulo: t('POWER_AGENTS.REASONING.OPTIONS.NONE'),
    dica: t('POWER_AGENTS.REASONING.HINTS.NONE'),
  },
  {
    valor: 'low',
    rotulo: t('POWER_AGENTS.REASONING.OPTIONS.LOW'),
    dica: t('POWER_AGENTS.REASONING.HINTS.LOW'),
  },
  {
    valor: 'medium',
    rotulo: t('POWER_AGENTS.REASONING.OPTIONS.MEDIUM'),
    dica: t('POWER_AGENTS.REASONING.HINTS.MEDIUM'),
  },
  {
    valor: 'high',
    rotulo: t('POWER_AGENTS.REASONING.OPTIONS.HIGH'),
    dica: t('POWER_AGENTS.REASONING.HINTS.HIGH'),
  },
]);

// Valor desconhecido (um nível que saiu da lista) cai no padrão, como o runtime faria.
const indice = computed(() =>
  Math.max(
    0,
    niveis.value.findIndex(n => n.valor === props.modelValue)
  )
);
const atual = computed(() => niveis.value[indice.value]);
const trancado = nivel => nivel.valor === 'none' && Boolean(props.bloqueadoPor);

function escolher(nivel) {
  if (!trancado(nivel)) emit('update:modelValue', nivel.valor);
}

// Setas andam pelos segmentos pulando o nível trancado, sem dar a volta; o foco vai junto.
const segmentos = ref([]);
async function teclado(evento) {
  const passo = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }[
    evento.key
  ];
  if (!passo) return;
  evento.preventDefault();
  const total = niveis.value.length;
  let i = indice.value;
  do i += passo;
  while (i >= 0 && i < total && trancado(niveis.value[i]));
  if (i < 0 || i >= total) return;
  escolher(niveis.value[i]);
  await nextTick();
  segmentos.value[i]?.focus();
}

const aviso = computed(() => {
  if (!props.bloqueadoPor) return '';
  return props.voltouAoPadrao
    ? t('POWER_AGENTS.REASONING.RESET', { modelo: props.bloqueadoPor })
    : t('POWER_AGENTS.REASONING.MANDATORY', { modelo: props.bloqueadoPor });
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      role="radiogroup"
      :aria-label="t('POWER_AGENTS.REASONING.TITLE')"
      class="flex items-stretch gap-0.5 p-0.5 rounded-lg bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
      @keydown="teclado"
    >
      <template v-for="(nivel, i) in niveis" :key="nivel.valor">
        <span
          v-if="i === 1"
          aria-hidden="true"
          class="w-px my-1.5 shrink-0 bg-n-strong"
        />
        <button
          ref="segmentos"
          type="button"
          role="radio"
          :aria-checked="i === indice"
          :aria-disabled="trancado(nivel)"
          :tabindex="i === indice ? 0 : -1"
          :title="trancado(nivel) ? aviso : nivel.dica"
          :data-nivel="nivel.valor"
          class="flex items-center justify-center flex-1 h-8 min-w-0 gap-1.5 px-1.5 text-xs border-0 rounded-md sm:text-sm transition-colors duration-150 ease-out outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-n-brand"
          :class="
            i === indice
              ? 'bg-n-solid-active shadow-sm font-medium text-n-slate-12'
              : trancado(nivel)
                ? 'cursor-not-allowed text-n-slate-9'
                : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
          "
          @click="escolher(nivel)"
        >
          <span
            v-if="i === indice"
            aria-hidden="true"
            class="rounded-full size-1.5 shrink-0 bg-n-brand"
          />
          <i
            v-else-if="trancado(nivel)"
            class="i-lucide-lock size-3 shrink-0"
          />
          <span class="truncate">{{ nivel.rotulo }}</span>
        </button>
      </template>
    </div>

    <p class="text-xs text-n-slate-11">{{ atual.dica }}</p>

    <p v-if="aviso" class="flex items-start gap-1.5 text-xs text-n-amber-11">
      <i class="i-lucide-lock size-3 mt-0.5 shrink-0" />
      {{ aviso }}
    </p>
  </div>
</template>
